import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Check, X, CheckCircle2, Clock } from 'lucide-react';
import { useI18n } from '@/lib/i18n';
import { useAuth } from '@/hooks/useAuth';
import { Button } from '@/components/ui/button';
import { showToast } from '@/components/ui/toaster';
import { hrRequestService, type ApprovalStep } from '@/services/hr-request.service';
import { formatRp } from '@/lib/currency';

const TYPE_LABEL_KEY: Record<string, string> = {
    overtime: 'ot.type.overtime',
    lateness_excuse: 'ot.type.lateness_excuse',
    reimbursement: 'ot.type.reimbursement',
    visit: 'ot.type.visit',
    sick_leave: 'ot.type.sick_leave',
    permission_leave: 'ot.type.permission_leave',
    annual_leave: 'ot.type.annual_leave',
    maternity_leave: 'ot.type.maternity_leave',
};

const STATUS_LABEL_KEY: Record<string, string> = {
    pending: 'ot.status.pending',
    approved: 'ot.status.approved',
    rejected: 'ot.status.rejected',
    cancelled: 'ot.status.cancelled',
};

export default function OvertimeDetailPage() {
    const { t } = useI18n();
    const { id } = useParams<{ id: string }>();
    const { user } = useAuth();
    const navigate = useNavigate();
    const [req, setReq] = useState<any | null>(null);
    const [busy, setBusy] = useState(false);

    const isRequester = req && user && req.requestedById === user._id;
    const canCancel = isRequester && req?.status === 'pending';

    // Rantai approval (multi-level). approvalStepsJson null = single-level lama
    // (admin tim mana pun approve). Saat chained, hanya current approver / delegate
    // yang boleh approve level ini — backend tetap yang menegakkan otorisasi.
    const steps: ApprovalStep[] = (() => {
        if (!req?.approvalStepsJson) return [];
        try { return JSON.parse(req.approvalStepsJson) as ApprovalStep[]; } catch { return []; }
    })();
    const isChained = steps.length > 0;
    const currentLevel = req?.approvalLevel ?? 0;
    const isCurrentApprover = req && user && req.currentApproverId === user._id;

    // Approve/reject: kalau chained, tampil hanya untuk current approver. Kalau
    // single-level, tampil untuk siapa pun yang bukan pemohon (backend gate admin
    // tim) — sama seperti perilaku approve lama di web.
    // Chained: tampil untuk current approver ATAU delegate aktif (canIApprove dari server).
    const canReview = req?.status === 'pending' && !isRequester
        && (isChained ? ((req as any)?.canIApprove ?? isCurrentApprover) : true);

    useEffect(() => {
        if (!id) return;
        hrRequestService.get(id).then((res) => setReq(res.data)).catch(() => {});
    }, [id]);

    const refresh = async () => {
        if (!id) return;
        const res = await hrRequestService.get(id);
        setReq(res.data);
    };

    const cancel = async () => {
        if (!id || !confirm(t('ot.detail.confirm_cancel'))) return;
        await hrRequestService.cancel(id);
        showToast({ description: t('ot.detail.toast_cancelled') });
        await refresh();
    };

    const approve = async () => {
        if (!id || busy) return;
        setBusy(true);
        try {
            await hrRequestService.approve(id);
            showToast({ description: t('ot.detail.toast_approved') });
            await refresh();
        } catch (e: any) {
            showToast({ variant: 'destructive', description: e?.response?.data?.error || t('ot.detail.toast_review_failed') });
        } finally {
            setBusy(false);
        }
    };

    const reject = async () => {
        if (!id || busy) return;
        const note = window.prompt(t('ot.detail.reject_prompt'))?.trim();
        if (!note) return;
        setBusy(true);
        try {
            await hrRequestService.reject(id, note);
            showToast({ description: t('ot.detail.toast_rejected') });
            await refresh();
        } catch (e: any) {
            showToast({ variant: 'destructive', description: e?.response?.data?.error || t('ot.detail.toast_review_failed') });
        } finally {
            setBusy(false);
        }
    };

    if (!req) {
        return (
            <div className="px-4 pt-12 max-w-md mx-auto text-center text-sm text-gray-400">
                {t('common.loading')}
            </div>
        );
    }

    return (
        <div className="px-4 pt-3 max-w-md mx-auto">
            <button onClick={() => navigate(-1)} className="flex items-center gap-1 text-sm text-gray-500 mb-3">
                <ArrowLeft className="h-4 w-4" /> {t('common.back')}
            </button>

            <div className="bg-white rounded-2xl p-5 space-y-3" style={{ border: '1px solid #e0d6cc' }}>
                <div className="flex items-start justify-between">
                    <div>
                        <h1 className="text-base font-bold">{t(TYPE_LABEL_KEY[req.type] || req.type)}</h1>
                        <p className="text-xs text-gray-500 mt-0.5">{req.team?.name || '—'}</p>
                    </div>
                    <span className={`text-[10px] font-bold px-2 py-1 rounded-full ${statusClass(req.status)}`}>
                        {t(STATUS_LABEL_KEY[req.status] || req.status)}
                    </span>
                </div>

                <div className="space-y-1 text-sm">
                    <Row label={t('ot.detail.requester')} value={req.requestedBy?.name || '—'} />
                    <Row label={t('ot.detail.from')} value={fmtDateTime(req.fromAt)} />
                    {req.toAt && <Row label={t('ot.detail.to')} value={fmtDateTime(req.toAt)} />}
                    {req.payload?.hours !== undefined && <Row label={t('ot.form.hours')} value={`${req.payload.hours} jam`} />}
                    {req.payload?.category && <Row label={t('ot.form.category')} value={req.payload.category} />}
                    {req.payload?.location && <Row label={t('ot.form.location')} value={req.payload.location} />}
                    {req.amount > 0 && <Row label={t('ot.form.amount')} value={formatRp(req.amount)} />}
                    {req.payload?.reason && (
                        <div>
                            <p className="text-xs text-gray-500 mt-2">{t('ot.form.reason')}</p>
                            <p className="text-sm bg-gray-50 rounded-lg p-2.5">{req.payload.reason}</p>
                        </div>
                    )}
                    {Array.isArray(req.payload?.attachments) && req.payload.attachments.length > 0 && (
                        <div>
                            <p className="text-xs text-gray-500 mt-2">{t('ot.form.attachments')}</p>
                            <ul className="space-y-1">
                                {req.payload.attachments.map((a: any, i: number) => (
                                    <li key={i}>
                                        <a href={a.url} target="_blank" rel="noopener" className="text-xs text-blue-600 underline">{a.filename}</a>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    )}
                </div>

                {req.reviewedBy && (
                    <div className="pt-3" style={{ borderTop: '1px solid #f0ebe5' }}>
                        <p className="text-xs text-gray-500">{t('ot.detail.reviewed_by')} {req.reviewedBy.name}</p>
                        {req.reviewNote && <p className="text-sm bg-gray-50 rounded-lg p-2.5 mt-1">{req.reviewNote}</p>}
                    </div>
                )}

                {/* Rantai approval multi-level — progres per level + posisi sekarang */}
                {isChained && req.status === 'pending' && (
                    <div className="pt-3" style={{ borderTop: '1px solid #f0ebe5' }}>
                        <p className="text-xs font-bold text-gray-700 mb-2">
                            {t('ot.detail.waiting_level', { level: currentLevel + 1, total: steps.length })}
                        </p>
                        <ol className="space-y-1.5">
                            {steps.map((s, i) => {
                                const done = i < currentLevel || s.status === 'approved';
                                const active = i === currentLevel;
                                return (
                                    <li key={i} className="flex items-center gap-2 text-xs">
                                        {done ? (
                                            <CheckCircle2 className="h-4 w-4 text-green-600 shrink-0" />
                                        ) : (
                                            <Clock className={`h-4 w-4 shrink-0 ${active ? 'text-amber-500' : 'text-gray-300'}`} />
                                        )}
                                        <span className={active ? 'font-bold text-gray-900' : done ? 'text-gray-500 line-through' : 'text-gray-400'}>
                                            {t('ot.detail.level_n', { n: i + 1 })}: {s.approverName || s.approverRole || s.approverUserId || '—'}
                                        </span>
                                    </li>
                                );
                            })}
                        </ol>
                    </div>
                )}

                {canReview && (
                    <div className="flex gap-2 pt-1">
                        <Button variant="outline" onClick={reject} disabled={busy} className="flex-1 text-red-600 border-red-200 hover:bg-red-50">
                            <X className="h-4 w-4 mr-1" /> {t('ot.detail.reject')}
                        </Button>
                        <Button onClick={approve} disabled={busy} className="flex-1">
                            <Check className="h-4 w-4 mr-1" /> {t('ot.detail.approve')}
                        </Button>
                    </div>
                )}

                {canCancel && (
                    <Button variant="outline" onClick={cancel} className="w-full">{t('ot.detail.cancel_request')}</Button>
                )}
            </div>
        </div>
    );
}

function Row({ label, value }: { label: string; value: any }) {
    return (
        <div className="flex justify-between">
            <span className="text-gray-500">{label}</span>
            <span className="font-bold text-right">{value}</span>
        </div>
    );
}
function fmtDateTime(ts: string) {
    return new Date(ts).toLocaleString('id-ID', { dateStyle: 'medium', timeStyle: 'short' });
}
function statusClass(s: string): string {
    return ({
        pending: 'bg-yellow-50 text-yellow-700',
        approved: 'bg-green-50 text-green-700',
        rejected: 'bg-red-50 text-red-700',
        cancelled: 'bg-gray-100 text-gray-500',
    } as any)[s] || 'bg-gray-100';
}
